import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { openStorageFile } from "@/lib/openStorageFile";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Clock, Paperclip, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "";
const isNew = (m) => {
  if (!m.publish_date || m.completed) return false;
  const diff = (Date.now() - new Date(m.publish_date).getTime()) / 86400000;
  return diff >= -1 && diff <= 7;
};
const openUrl = (u) => window.open(u, "_blank", "noopener,noreferrer");
const openFile = async (m) => { try { await openStorageFile(m.attachment_path); } catch (e) { toast.error(e.message || "Impossible d'ouvrir le fichier"); } };

export default function LearnerMissions() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState({ status: "all" });

  const load = () => {
    const params = {};
    if (filter.status !== "all") params.status = filter.status;
    api.get("/learner/missions", { params }).then(({ data }) => setItems(data));
  };
  useEffect(() => { load(); }, [filter]);

  const toggle = async (m) => {
    await api.patch(`/learner/missions/${m.id}/complete`, { completed: !m.completed });
    load();
  };

  return (
    <div className="lg:ml-56">
      <h1 className="font-serif-accent text-3xl sm:text-4xl">Mes missions</h1>
      <p className="text-[#5C6561] mt-1 mb-5 text-sm">Cochez chaque mission une fois terminée.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <Tabs value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <TabsList className="bg-[#F4EFEA]">
            <TabsTrigger value="all" data-testid="mission-filter-all">Toutes</TabsTrigger>
            <TabsTrigger value="todo" data-testid="mission-filter-todo">À faire</TabsTrigger>
            <TabsTrigger value="completed" data-testid="mission-filter-completed">Terminées</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <div className="dec-card p-6 text-center text-[#5C6561]">Aucune mission.</div>}
        {items.map(m => (
          <div key={m.id} className="dec-card p-4 flex items-center gap-4" data-testid={`learner-mission-${m.id}`}>
            <input type="checkbox" checked={m.completed} onChange={() => toggle(m)} className="w-5 h-5 accent-[#D95D39]" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-semibold ${m.completed ? "line-through text-[#5C6561]" : ""}`}>{m.title}</span>
                {isNew(m) && <span className="dec-pill text-[10px] px-2 py-0.5 bg-[#D95D39] text-white" data-testid={`mission-new-badge-${m.id}`}>Nouvelle</span>}
              </div>
              {m.description && <div className="text-xs text-[#5C6561] mt-0.5 line-clamp-2">{m.description}</div>}
              <div className="flex flex-wrap gap-3 mt-1 text-[11px] text-[#5C6561]">
                {m.publish_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Publiée le {fmtDate(m.publish_date)}</span>}
                {m.deadline && <span className="inline-flex items-center gap-1 text-[#C04E2D]"><Clock className="w-3 h-3" /> À rendre le {fmtDate(m.deadline)}</span>}
              </div>
              {(m.attachment_url || m.attachment_path) && (
                <div className="mt-2 flex flex-wrap gap-3">
                  {m.attachment_url && (
                    <button onClick={() => openUrl(m.attachment_url)} className="text-xs text-[#D95D39] font-medium inline-flex items-center gap-1" data-testid={`learner-mission-link-${m.id}`}>
                      <ExternalLink className="w-3 h-3" /> Lien
                    </button>
                  )}
                  {m.attachment_path && (
                    <button onClick={() => openFile(m)} className="text-xs text-[#D95D39] font-medium inline-flex items-center gap-1" data-testid={`learner-mission-attach-${m.id}`}>
                      <Paperclip className="w-3 h-3" /> {m.attachment_name || "Fichier"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
